import type { KeyboardEvent, MouseEvent } from 'react';

import { FromPriceDisplay } from '../PriceDisplay';
import {
  ActionButton,
  Content,
  Header,
  Image,
  ImageBadge,
  ImageWrapper,
  ItemCardBase,
  Meta,
  Placeholder,
  PriceRow,
  StatusBadge,
  Title,
} from './ItemCard.styles';
import type { ItemCardProps } from './ItemCard.types';

export function ItemCard({
  image,
  title,
  meta,
  price,
  statusBadge,
  imageBadge,
  action,
  unavailable,
  ariaLabel,
  onSelect,
}: ItemCardProps) {
  const clickable = Boolean(onSelect);

  const trigger = () => {
    if (unavailable) return;
    if (onSelect) onSelect();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      trigger();
    }
  };

  return (
    <ItemCardBase
      $unavailable={unavailable}
      $clickable={clickable}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? (unavailable ? -1 : 0) : undefined}
      aria-disabled={unavailable || undefined}
      aria-label={ariaLabel}
      onClick={clickable ? trigger : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    >
      <ImageWrapper>
        {image.src ? (
          <Image src={image.src} alt={image.alt} />
        ) : (
          <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
        )}
        {imageBadge && <ImageBadge>{imageBadge}</ImageBadge>}
      </ImageWrapper>

      <Content>
        <Header>
          <Title>{title}</Title>
          {action && (
            <ActionButton
              type="button"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                action.onClick();
              }}
            >
              {action.label}
            </ActionButton>
          )}
        </Header>

        {meta && <Meta>{meta}</Meta>}

        {statusBadge && <StatusBadge>{statusBadge}</StatusBadge>}

        {price && (
          <PriceRow>
            <FromPriceDisplay
              amount={price.amount}
              currencyCode={price.currencyCode}
              label={price.label}
              discount={price.discount}
              locale={price.locale}
              size="small"
            />
          </PriceRow>
        )}
      </Content>
    </ItemCardBase>
  );
}
